import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation, Trans } from 'react-i18next';
import { formatPrice } from '../lib/money';
import { Calendar, MapPin, ArrowRight } from 'lucide-react';
import { useMe, useMyBookings, MyBooking } from '../api/use-marketplace';
import { SiteShell, formatAddress } from '../components/site-shell';

/**
 * /me/bookings. Auth-gated. Two tabs: Upcoming / Past. Past defaults to
 * collapsed-until-clicked so the page opens calm.
 */
export function MyBookingsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
  const me = useMe();
  const upcoming = useMyBookings(false);
  const past = useMyBookings(true);
  const nav = useNavigate();

  // Auth gate. Reload after sign-out → redirect to login.
  useEffect(() => {
    if (!me.isLoading && me.data === null) {
      nav('/login?next=/me/bookings', { replace: true });
    }
  }, [me.isLoading, me.data, nav]);

  if (me.isLoading || !me.data) {
    return (
      <SiteShell>
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <div className="w-10 h-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin mx-auto" />
        </div>
      </SiteShell>
    );
  }

  const active = tab === 'upcoming' ? upcoming : past;
  const user = me.data;

  return (
    <SiteShell>
      <section className="max-w-3xl mx-auto px-6 py-12 md:py-16">
        <div className="flex items-center justify-between gap-4 mb-3">
          <p className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
            {t('myBookings.signedInAs', { email: user.email })}
          </p>
          <Link
            to="/me/profile"
            className="text-xs font-medium text-primary hover:underline whitespace-nowrap"
          >
            {t('profile.edit')}
          </Link>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-8">
          {t('myBookings.title')}
        </h1>

        <div className="inline-flex p-1 rounded-full border border-border/60 bg-card mb-8">
          <TabButton active={tab === 'upcoming'} onClick={() => setTab('upcoming')}>
            {t('myBookings.tabUpcoming')}
          </TabButton>
          <TabButton active={tab === 'past'} onClick={() => setTab('past')}>
            {t('myBookings.tabPast')}
          </TabButton>
        </div>

        {active.isLoading && (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-24 rounded-2xl bg-muted/40 animate-pulse" />
            ))}
          </div>
        )}

        {!active.isLoading && active.data && active.data.length === 0 && (
          <EmptyState type={tab} />
        )}

        {!active.isLoading && active.data && active.data.length > 0 && (
          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {active.data.map((b) => (
              <li key={b.id}>
                <BookingCard booking={b} past={tab === 'past'} />
              </li>
            ))}
          </motion.ul>
        )}
      </section>
    </SiteShell>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'px-5 py-2 rounded-full text-sm font-medium transition-colors',
        active
          ? 'bg-foreground text-background'
          : 'text-muted-foreground hover:text-foreground',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function BookingCard({ booking, past }: { booking: MyBooking; past: boolean }) {
  const { t, i18n } = useTranslation();
  const date = new Date(booking.startTime);
  // Only link to the salon page when the salon is still listed on the
  // marketplace — otherwise GET /marketplace/salon/{subdomain} 404s and the
  // page shows nothing. Delisted (or missing) salon → render a static card.
  const clickable = !!booking.tenant && booking.tenant.listed !== false;
  const baseClass =
    'group block rounded-2xl border border-border/60 bg-card p-5 md:p-6 transition-all';
  const inner = (
    <>
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">
            {past ? t('myBookings.statusPast') : t('myBookings.statusUpcoming')} ·{' '}
            <StatusPill status={booking.status} />
          </p>
          <h3 className="text-xl font-bold text-foreground group-hover:text-primary transition-colors mb-1.5">
            {booking.service?.name ?? t('common.loading')}
          </h3>
          {booking.tenant && (
            <p className="text-sm text-muted-foreground mb-3">
              <Trans
                i18nKey="myBookings.atSalon"
                values={{ name: booking.tenant.name }}
                components={{ s: <span className="text-foreground" /> }}
              />
            </p>
          )}
          <div className="space-y-1.5 text-sm">
            <p className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-4 h-4 flex-shrink-0" />
              <span>{formatDateTime(date, i18n.language)}</span>
            </p>
            {booking.location?.address && (
              <p className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{formatAddress(booking.location.address)}</span>
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {booking.service?.price != null && (
            <span className="font-semibold text-primary tabular-nums">
              {formatPrice(booking.service.price, booking.currency, i18n.language)}
            </span>
          )}
          {clickable && (
            <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
          )}
        </div>
      </div>
    </>
  );

  if (!clickable) {
    return <div className={baseClass}>{inner}</div>;
  }
  return (
    <Link
      to={`/salon/${booking.tenant!.subdomain}`}
      className={`${baseClass} hover:border-primary/40 hover:shadow-sm`}
    >
      {inner}
    </Link>
  );
}

function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  return <span className="text-foreground/80">{t(`myBookings.status.${status}`, { defaultValue: status })}</span>;
}

function EmptyState({ type }: { type: 'upcoming' | 'past' }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl border border-dashed border-border/80 bg-background/50 p-10 md:p-12 text-center">
      <h3 className="text-2xl font-bold text-foreground mb-3">
        {type === 'upcoming' ? t('myBookings.emptyUpcomingTitle') : t('myBookings.emptyPastTitle')}
      </h3>
      <p className="text-muted-foreground max-w-sm mx-auto leading-relaxed mb-6">
        {type === 'upcoming' ? t('myBookings.emptyUpcomingBody') : t('myBookings.emptyPastBody')}
      </p>
      {type === 'upcoming' && (
        <Link
          to="/search"
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
        >
          {t('myBookings.browseSalons')}
          <ArrowRight className="w-4 h-4" />
        </Link>
      )}
    </div>
  );
}

function formatDateTime(date: Date, locale: string): string {
  // Locale-aware, short: "Tue 21 May, 14:30"
  try {
    return new Intl.DateTimeFormat(locale || 'en', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return date.toLocaleString();
  }
}
